import { getPartnerCurrency, toGalaxyFrequency } from '@vendasta/billing-ui';
import { AppPrices, FrequencyPrice, PriceDisplayOverride, UnitBillingFrequency } from '@vendasta/marketplace-apps';
import { ItemBasePrice } from './base';
import { ItemFrequencyPrice, calculateUnitPriceFromInstancePrice, getFrequencyPrices } from './frequency';
import { InventoryItemWithPricing } from '../inventory-item';

export type ItemWholesalePrice = ItemBasePrice & {
  frequencyPrices: ItemFrequencyPrice[];
  usesVariablePricing?: boolean;
  contactSales?: boolean;
};

export type ParseWholesalePriceConfig = {
  appPrices: AppPrices;
  item: InventoryItemWithPricing;
};

export function parseWholesaleItemPrice({ appPrices, item }: ParseWholesalePriceConfig): ItemWholesalePrice {
  const editionId = item.type === 'app' ? item.edition?.editionId : undefined;
  const appPrice = appPrices?.pricesForContexts?.['wholesale'];
  const unitPrice = appPrice?.pricesForEditions?.[editionId ?? ''];
  const marketplaceFrequencyPrices = getFrequencyPrices(appPrices, editionId, 'wholesale');

  const usesVariablePricing = appPrice?.billingConfiguration?.usesVariablePricing;

  return {
    frequencyPrices: parseWholesaleFrequencyPrices({
      marketplaceFrequencyPrices: marketplaceFrequencyPrices,
      item: item,
      usesVariablePricing,
    }),
    currency: getPartnerCurrency(appPrice),
    usesVariablePricing: appPrice?.billingConfiguration?.usesVariablePricing,
    contactSales: appPrice?.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES,
    discounts: unitPrice?.discount ?? [],
  };
}

type parseWholesaleFrequencyPrices = {
  marketplaceFrequencyPrices: FrequencyPrice[];
  item: InventoryItemWithPricing;
  usesVariablePricing: boolean;
};

function parseWholesaleFrequencyPrices({
  marketplaceFrequencyPrices,
  item: item,
  usesVariablePricing,
}: parseWholesaleFrequencyPrices): ItemFrequencyPrice[] {
  return marketplaceFrequencyPrices.map<ItemFrequencyPrice>((fp) => {
    const minimumPrice = fp?.pricingRules?.[0].price || 0;
    let price = minimumPrice;

    if (usesVariablePricing && item.pricing?.cost?.customPrice) {
      price = item.pricing.cost.customPrice;
    }

    const hasInstancePrice = Boolean(fp?.instancePrice);
    if (hasInstancePrice && !usesVariablePricing) {
      price = calculateUnitPriceFromInstancePrice(fp.instancePrice, fp.setupFee, item.quantity);
    }

    return {
      price: price,
      total: price * item.quantity,
      frequency: toGalaxyFrequency(fp?.frequency ?? UnitBillingFrequency.UNIT_BILLING_FREQUENCY_ONETIME),
      minimumPrice: minimumPrice,
    };
  });
}
